import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import AdminAbonnements from '../components/AdminAbonnements';
import AdminHistorique from '../components/AdminHistorique';
import logo from '../logo.png';
import fleur from '../fleur.png';

// ═══ Hook responsive ═══
const useResponsive = () => {
    const getState = () => {
        const w = window.innerWidth;
        return { isSmall: w <= 480, isMobile: w <= 900, isTablet: w > 900 && w <= 1200 };
    };
    const [state, setState] = useState(getState);
    useEffect(() => {
        const handleResize = () => setState(getState());
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    return state;
};

// Onglets de l'administration
const ONGLETS = [
    { id: 'stats', label: 'Tableau de bord' },
    { id: 'members', label: 'Membres inscrits' },
    { id: 'messages', label: 'Modération' },
    { id: 'contact', label: 'Contact reçus' },
    { id: 'abonnements', label: 'Abonnements' },
    { id: 'historique', label: 'Échanges' },
];

const Admin = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const { isSmall, isMobile } = useResponsive();
    const [menuOpen, setMenuOpen] = useState(false);
    const [members, setMembers] = useState([]);
    const [stats, setStats] = useState(null);
    const [pendingMessages, setPendingMessages] = useState([]);
    const [contactMessages, setContactMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    // Un modérateur ne voit que l'onglet Modération ; on force donc l'onglet actif.
    const isModerateur = user?.role === 'moderateur';
    const [activeTab, setActiveTab] = useState(user?.role === 'moderateur' ? 'messages' : 'stats');
    // Onglets visibles selon le rôle : le modérateur ne voit que "Modération".
    const ongletsVisibles = user?.role === 'moderateur'
        ? ONGLETS.filter(o => o.id === 'messages')
        : ONGLETS;

    // Ouverture directe d'un onglet depuis une notification : /admin?onglet=messages
    // On se cale sur location.key plutôt que sur la seule adresse, pour que
    // recliquer sur la même notification fonctionne aussi.
    // L'adresse est nettoyée ensuite, sinon un changement d'onglet manuel
    // serait annulé au prochain rendu.
    const location = useLocation();
    useEffect(() => {
        const demande = new URLSearchParams(location.search).get('onglet');
        if (demande && ONGLETS.some(o => o.id === demande)) {
            setActiveTab(demande);
            navigate('/admin', { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.key]);
    // Modale de rejet de message avec motif
    const [rejectTarget, setRejectTarget] = useState(null);
    const [rejectMotif, setRejectMotif] = useState('');
    const [rejectComment, setRejectComment] = useState('');
    const [rejectLoading, setRejectLoading] = useState(false);
    const [rejectError, setRejectError] = useState('');
    // Horaires de modération
    const [horaires, setHoraires] = useState({ heure_debut: 10, heure_fin: 22, actif: true, ouvert: true, jour_ferme: null });
    const [horairesEdit, setHorairesEdit] = useState({ heure_debut: 10, heure_fin: 22, actif: true, jour_ferme: null });
    const [horairesMsg, setHorairesMsg] = useState('');
    const [statsRelations, setStatsRelations] = useState(null);
    const [statsAbonnements, setStatsAbonnements] = useState(null);
    // Consultation du profil d'un membre (modale) + refus avec motif
    const [profilVu, setProfilVu] = useState(null);      // données du profil affiché
    const [profilLoading, setProfilLoading] = useState(false);
    const [refusTarget, setRefusTarget] = useState(null); // membre en cours de refus
    const [refusMotif, setRefusMotif] = useState('');
    const [refusLoading, setRefusLoading] = useState(false);

    // Fige le défilement de la page derrière quand une modale est ouverte
    useEffect(() => {
        const ouverte = profilVu || refusTarget;
        document.body.style.overflow = ouverte ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [profilVu, refusTarget]);
    // Filtres et pagination du tableau des membres
    const [mRecherche, setMRecherche] = useState('');
    const [mStatut, setMStatut] = useState('');      // '', 'valides', 'attente'
    const [mGenre, setMGenre] = useState('');        // '', 'homme', 'femme'
    const [mVille, setMVille] = useState('');
    const [mPage, setMPage] = useState(1);
    // Gestion des motifs (chargés depuis la base, éditables)
    const [motifs, setMotifs] = useState([]);
    const [gestionMotifs, setGestionMotifs] = useState(false); // mode édition dans la modale
    const [nouveauMotif, setNouveauMotif] = useState('');
    const [motifEnEdition, setMotifEnEdition] = useState(null); // { id, libelle }

    useEffect(() => {
        fetchStats();
        fetchMembers();
        fetchPendingMessages();
        fetchContactMessages();
        fetchHoraires();
        fetchStatsRelations();
        fetchStatsAbonnements();
    }, []);

    const fetchStats = async () => {
        try {
            const response = await api.get('/admin/stats');
            setStats(response.data);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchMembers = async () => {
        try {
            const response = await api.get('/admin/members');
            setMembers(response.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchPendingMessages = async () => {
        try {
            const response = await api.get('/admin/messages/pending');
            setPendingMessages(response.data);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchContactMessages = async () => {
        try {
            const response = await api.get('/contact');
            setContactMessages(response.data);
        } catch (err) {
            console.error(err);
        }
    };

    const handleValidate = async (id) => {
        try {
            await api.put(`/admin/members/${id}/validate`);
            fetchMembers();
            fetchStats();
        } catch (err) {
            console.error(err);
        }
    };

    // Charger et afficher la fiche complète d'un membre (l'admin est exempté du contrôle d'accès)
    const handleVoirProfil = async (member) => {
        setProfilLoading(true);
        setProfilVu({ __chargement: true, email: member.email });
        try {
            const response = await api.get(`/profiles/${member.id}`);
            setProfilVu({ ...response.data, email: member.email, gender: member.gender });
        } catch (err) {
            setProfilVu({ __erreur: true, email: member.email });
        } finally {
            setProfilLoading(false);
        }
    };

    // Refuser un profil avec un motif (le membre reçoit un email et pourra re-soumettre)
    const handleRefuser = async () => {
        if (!refusMotif.trim()) return;
        setRefusLoading(true);
        try {
            await api.put(`/admin/members/${refusTarget.id}/refuse`, { motif: refusMotif.trim() });
            setRefusTarget(null);
            setRefusMotif('');
            fetchMembers();
            fetchStats();
        } catch (err) {
            console.error(err);
        } finally {
            setRefusLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Êtes-vous sûr de vouloir supprimer ce membre ?')) {
            try {
                await api.delete(`/admin/members/${id}`);
                fetchMembers();
                fetchStats();
            } catch (err) {
                console.error(err);
            }
        }
    };

    // Nommer un membre modérateur, ou le rétrograder en membre simple.
    const handleChangeRole = async (id, nouveauRole) => {
        const action = nouveauRole === 'moderateur' ? 'nommer ce membre modérateur' : 'retirer le rôle de modérateur';
        if (!window.confirm(`Voulez-vous ${action} ?`)) return;
        try {
            await api.put(`/admin/members/${id}/role`, { role: nouveauRole });
            fetchMembers();
        } catch (err) {
            console.error(err);
            alert(err.response?.data?.message || 'Erreur lors du changement de rôle');
        }
    };

    const handleApproveMessage = async (id) => {
        try {
            await api.put(`/admin/messages/${id}/approve`);
            fetchPendingMessages();
            fetchStats();
        } catch (err) {
            console.error(err);
        }
    };

    // Charge la liste des motifs depuis la base
    const fetchMotifs = async () => {
        try {
            const response = await api.get('/admin/motifs');
            setMotifs(response.data);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchStatsAbonnements = async () => {
        try {
            const response = await api.get('/admin/abonnements/membres');
            const liste = response.data || [];
            const actifs = liste.filter(a => a.statut === 'actif');
            setStatsAbonnements({
                actifs: actifs.length,
                total: liste.length,
                revenus: liste.reduce((s, a) => s + parseFloat(a.prix_paye || 0), 0),
                hommes: actifs.filter(a => a.gender === 'homme').length,
                femmes: actifs.filter(a => a.gender === 'femme').length,
            });
        } catch (err) {
            console.error(err);
        }
    };

    const fetchStatsRelations = async () => {
        try {
            const response = await api.get('/admin/relations/stats');
            setStatsRelations(response.data);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchHoraires = async () => {
        try {
            const response = await api.get('/moderation');
            setHoraires(response.data);
            setHorairesEdit({
                heure_debut: response.data.heure_debut,
                heure_fin: response.data.heure_fin,
                actif: response.data.actif,
                jour_ferme: response.data.jour_ferme ?? null,
            });
        } catch (err) {
            console.error(err);
        }
    };

    const handleSaveHoraires = async () => {
        setHorairesMsg('');
        try {
            const response = await api.put('/moderation', horairesEdit);
            setHoraires(response.data);
            setHorairesMsg('Horaires enregistrés.');
            setTimeout(() => setHorairesMsg(''), 4000);
        } catch (err) {
            setHorairesMsg(err.response?.data?.message || 'Erreur lors de l\'enregistrement');
        }
    };

    const handleAjouterMotif = async () => {
        if (!nouveauMotif.trim()) return;
        try {
            await api.post('/admin/motifs', { libelle: nouveauMotif.trim() });
            setNouveauMotif('');
            fetchMotifs();
        } catch (err) {
            setRejectError(err.response?.data?.message || 'Erreur lors de l\'ajout');
        }
    };

    const handleModifierMotif = async () => {
        if (!motifEnEdition || !motifEnEdition.libelle.trim()) return;
        try {
            await api.put(`/admin/motifs/${motifEnEdition.id}`, { libelle: motifEnEdition.libelle.trim() });
            setMotifEnEdition(null);
            fetchMotifs();
        } catch (err) {
            setRejectError(err.response?.data?.message || 'Erreur lors de la modification');
        }
    };

    const handleSupprimerMotif = async (id) => {
        try {
            await api.delete(`/admin/motifs/${id}`);
            fetchMotifs();
        } catch (err) {
            setRejectError(err.response?.data?.message || 'Erreur lors de la suppression');
        }
    };

    // Ouvre la modale de saisie du motif (le rejet réel se fait après validation du motif)
    const openRejectModal = (msg) => {
        setRejectTarget(msg);
        setRejectMotif('');
        setRejectComment('');
        setRejectError('');
        setGestionMotifs(false);
        fetchMotifs();
    };

    const handleConfirmReject = async () => {
        if (!rejectTarget) return;
        // Motif final : le motif choisi, complété du commentaire éventuel
        const motifFinal = rejectComment.trim()
            ? (rejectMotif ? `${rejectMotif} — ${rejectComment.trim()}` : rejectComment.trim())
            : rejectMotif;

        if (!motifFinal) {
            setRejectError('Veuillez choisir un motif ou saisir un commentaire.');
            return;
        }

        setRejectLoading(true);
        setRejectError('');
        try {
            await api.put(`/admin/messages/${rejectTarget.id}/reject`, { motif: motifFinal });
            setRejectTarget(null);
            fetchPendingMessages();
        } catch (err) {
            setRejectError(err.response?.data?.message || 'Erreur lors du rejet');
        } finally {
            setRejectLoading(false);
        }
    };

    const handleMarkRead = async (id) => {
        try {
            await api.put(`/contact/${id}/read`);
            fetchContactMessages();
        } catch (err) {
            console.error(err);
        }
    };

    const handleDeleteContact = async (id) => {
        if (window.confirm('Supprimer ce message de contact ?')) {
            try {
                await api.delete(`/contact/${id}`);
                fetchContactMessages();
            } catch (err) {
                console.error(err);
            }
        }
    };

    // ═══ Statistiques calculées sur les membres ═══
    // Inscrits depuis le début du mois en cours
    const nouveauxCeMois = members.filter(m => {
        if (!m.created_at) return false;
        const d = new Date(m.created_at);
        const now = new Date();
        return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).length;

    // Un profil est considéré comme complété s'il a au moins un prénom et une ville
    const profilsComplets = members.filter(m => m.first_name && m.city).length;

    // Ville la plus représentée
    const villePrincipale = (() => {
        const compte = {};
        members.forEach(m => {
            if (m.city) compte[m.city] = (compte[m.city] || 0) + 1;
        });
        const trie = Object.entries(compte).sort((a, b) => b[1] - a[1]);
        return trie.length > 0
            ? { nom: trie[0][0], total: trie[0][1] }
            : { nom: '—', total: 0 };
    })();

    // ═══ Filtrage et pagination du tableau des membres ═══
    const PAR_PAGE = 10;

    const membresFiltres = members.filter(m => {
        if (mStatut === 'valides' && !m.is_validated) return false;
        if (mStatut === 'attente' && m.is_validated) return false;
        if (mGenre && m.gender !== mGenre) return false;
        if (mVille && m.city !== mVille) return false;
        if (mRecherche.trim()) {
            // Le croisillon est retiré : la fiche affiche « #223389 », un admin
            // le recopiera tel quel.
            const q = mRecherche.trim().toLowerCase().replace('#', '');
            const champs = [m.first_name, m.email, m.city, m.numero_membre]
                .filter(Boolean).join(' ').toLowerCase();
            if (!champs.includes(q)) return false;
        }
        return true;
    });

    const mTotalPages = Math.max(1, Math.ceil(membresFiltres.length / PAR_PAGE));
    const mPageCourante = Math.min(mPage, mTotalPages);
    const membresAffiches = membresFiltres.slice(
        (mPageCourante - 1) * PAR_PAGE,
        mPageCourante * PAR_PAGE
    );

    // Villes présentes, pour alimenter le filtre
    const villesDisponibles = [...new Set(members.map(m => m.city).filter(Boolean))].sort();

    const mFiltresActifs = [mRecherche, mStatut, mGenre, mVille].filter(Boolean).length;

    // En colonne (mobile), on retire le flex-grow qui étirait les champs et créait de grands vides.
    const filtreLabelStyle = { ...styles.filtreLabel, flex: isSmall ? '0 0 auto' : '1 1 160px' };

    const resetFiltresMembres = () => {
        setMRecherche(''); setMStatut(''); setMGenre(''); setMVille(''); setMPage(1);
    };

    // Pastille de notification par onglet
    const compteurOnglet = (id) => {
        if (id === 'messages') return pendingMessages.length;
        if (id === 'contact') return contactMessages.filter(m => !m.is_read).length;
        return 0;
    };
    const totalNotifications = pendingMessages.length + contactMessages.filter(m => !m.is_read).length;

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    return (
        <div style={styles.container}>
            {/* ═══ NAVBAR style NavbarAuth ═══ */}
            <nav style={{
                ...styles.navbar,
                height: isMobile ? '110px' : '82px',
                padding: isMobile ? '0 14px' : '0 48px',
                backgroundImage: isMobile ? 'none' : `url(${fleur})`,
                justifyContent: isMobile ? 'center' : 'space-between',
            }}>
                <img
                    src={logo}
                    alt="logo"
                    onClick={() => { setActiveTab('stats'); setMenuOpen(false); }}
                    style={{
                        ...styles.navLogo,
                        height: isMobile ? '100px' : '70px',
                        width: 'auto',
                        maxWidth: isMobile ? '75%' : 'none',
                        objectFit: 'contain',
                        marginTop: 0,
                        marginLeft: isMobile ? '0' : '50px',
                        minWidth: 0,
                    }}
                />

                {/* Menu horizontal sur grand écran */}
                {!isMobile && (
                    <div style={styles.navCenter}>
                        {ongletsVisibles.map(o => (
                            <button
                                key={o.id}
                                onClick={() => setActiveTab(o.id)}
                                style={activeTab === o.id ? styles.tabActive : styles.tab}
                            >
                                {o.label}
                                {compteurOnglet(o.id) > 0 && (
                                    <span style={styles.badge}>{compteurOnglet(o.id)}</span>
                                )}
                            </button>
                        ))}
                    </div>
                )}

                <div style={{
                    ...styles.navRight,
                    ...(isMobile ? { position: 'absolute', right: '14px' } : {}),
                }}>
                    {/* Sur mobile : burger seul. Sur grand écran : déconnexion. */}
                    {isMobile ? (
                        <button
                            onClick={() => setMenuOpen(!menuOpen)}
                            style={styles.burgerBtn}
                            title="Menu"
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                                stroke="white" strokeWidth="2" strokeLinecap="round">
                                {menuOpen ? (
                                    <><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>
                                ) : (
                                    <><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></>
                                )}
                            </svg>
                            {totalNotifications > 0 && !menuOpen && (
                                <span style={styles.badge}>{totalNotifications}</span>
                            )}
                        </button>
                    ) : (
                        <>
                            {user && (
                                <span style={styles.userConnecte}>
                                    {user.first_name || user.email}
                                </span>
                            )}
                            <button onClick={handleLogout} style={styles.logoutBtn} title="Déconnexion">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                                    stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
                                    <line x1="12" y1="2" x2="12" y2="12" />
                                </svg>
                            </button>
                        </>
                    )}
                </div>
            </nav>

            {/* Menu déroulant sur mobile */}
            {isMobile && menuOpen && (
                <div style={styles.mobileMenu}>
                    {user && (
                        <div style={styles.userConnecteMobile}>
                            {user.first_name || user.email}
                        </div>
                    )}
                    {ongletsVisibles.map(o => (
                        <button
                            key={o.id}
                            onClick={() => { setActiveTab(o.id); setMenuOpen(false); }}
                            style={activeTab === o.id ? styles.mobileTabActive : styles.mobileTab}
                        >
                            {o.label}
                            {compteurOnglet(o.id) > 0 && (
                                <span style={styles.mobileBadge}>{compteurOnglet(o.id)}</span>
                            )}
                        </button>
                    ))}

                    <button onClick={handleLogout} style={styles.mobileLogout}>
                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none"
                            stroke="#C4688A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
                            <line x1="12" y1="2" x2="12" y2="12" />
                        </svg>
                        Déconnexion
                    </button>
                </div>
            )}

            <div style={{ ...styles.content, padding: isSmall ? '16px 12px' : isMobile ? '20px 16px' : '32px' }}>
                {activeTab === 'stats' && stats && (
                    <div>
                        <h2 style={{ ...styles.title, fontSize: isSmall ? '23px' : isMobile ? '26px' : '30px', textAlign: isMobile ? 'center' : 'left' }}>Tableau de bord</h2>

                        {/* ═══ Membres ═══ */}
                        <h3 style={{ ...styles.sousTitre, textAlign: isMobile ? 'center' : 'left' }}>Membres inscrits</h3>
                        <div style={{ ...styles.statsGrid, gridTemplateColumns: isSmall ? '1fr 1fr' : 'repeat(auto-fit, minmax(190px, 1fr))', gap: isSmall ? '12px' : '20px' }}>
                            <div style={styles.statCard}>
                                <div style={styles.statNumber}>{stats.total_members}</div>
                                <div style={styles.statLabel}>Total membres</div>
                            </div>
                            <div style={styles.statCard}>
                                <div style={styles.statNumber}>{stats.pending_members}</div>
                                <div style={styles.statLabel}>En attente de validation</div>
                            </div>
                            <div style={styles.statCard}>
                                <div style={styles.statNumber}>
                                    {members.filter(m => m.gender === 'homme').length}
                                </div>
                                <div style={styles.statLabel}>Hommes</div>
                            </div>
                            <div style={styles.statCard}>
                                <div style={styles.statNumber}>
                                    {members.filter(m => m.gender === 'femme').length}
                                </div>
                                <div style={styles.statLabel}>Femmes</div>
                            </div>
                        </div>

                        {/* ═══ Modération ═══ */}
                        <h3 style={{ ...styles.sousTitre, textAlign: isMobile ? 'center' : 'left' }}>Modération des messages</h3>
                        <div style={{ ...styles.statsGrid, gridTemplateColumns: isSmall ? '1fr 1fr' : 'repeat(auto-fit, minmax(190px, 1fr))', gap: isSmall ? '12px' : '20px' }}>
                            <div style={styles.statCard}>
                                <div style={styles.statNumber}>{stats.total_messages}</div>
                                <div style={styles.statLabel}>Messages échangés</div>
                            </div>
                            <div style={styles.statCard}>
                                <div style={styles.statNumber}>{pendingMessages.length}</div>
                                <div style={styles.statLabel}>En attente de modération</div>
                            </div>
                            <div style={styles.statCard}>
                                <div style={{ ...styles.statTexte, fontSize: isSmall ? '22px' : isMobile ? '26px' : '34px', whiteSpace: 'nowrap', textOverflow: 'clip' }}>
                                    {horaires.actif
                                        ? `${String(horaires.heure_debut).padStart(2, '0')}h à ${String(horaires.heure_fin).padStart(2, '0')}h`
                                        : '24h/24'}
                                </div>
                                <div style={styles.statLabel}>Horaires de modération</div>
                            </div>
                            <div style={styles.statCard}>
                                <div style={{ ...styles.statTexte, fontSize: isSmall ? '22px' : isMobile ? '26px' : '34px', whiteSpace: 'normal', textOverflow: 'clip' }}>
                                    {horaires.ouvert ? 'Ouvert' : 'Fermé'}
                                </div>
                                <div style={styles.statLabel}>État actuel</div>
                            </div>
                        </div>

                        {/* ═══ Contact ═══ */}
                        <h3 style={{ ...styles.sousTitre, textAlign: isMobile ? 'center' : 'left' }}>Messages de contact</h3>
                        <div style={{ ...styles.statsGrid, gridTemplateColumns: isSmall ? '1fr 1fr' : 'repeat(auto-fit, minmax(190px, 1fr))', gap: isSmall ? '12px' : '20px' }}>
                            <div style={styles.statCard}>
                                <div style={styles.statNumber}>{contactMessages.length}</div>
                                <div style={styles.statLabel}>Total reçus</div>
                            </div>
                            <div style={styles.statCard}>
                                <div style={styles.statNumber}>
                                    {contactMessages.filter(m => !m.is_read).length}
                                </div>
                                <div style={styles.statLabel}>Non lus</div>
                            </div>
                        </div>

                        {/* ═══ Échanges ═══ */}
                        {statsRelations && (
                            <>
                                <h3 style={{ ...styles.sousTitre, textAlign: isMobile ? 'center' : 'left' }}>Échanges entre membres</h3>
                                <div style={{ ...styles.statsGrid, gridTemplateColumns: isSmall ? '1fr 1fr' : 'repeat(auto-fit, minmax(190px, 1fr))', gap: isSmall ? '12px' : '20px' }}>
                                    <div style={styles.statCard}>
                                        <div style={styles.statNumber}>{statsRelations.en_cours}</div>
                                        <div style={styles.statLabel}>Échanges en cours</div>
                                    </div>
                                    <div style={styles.statCard}>
                                        <div style={styles.statNumber}>{statsRelations.terminees}</div>
                                        <div style={styles.statLabel}>Échanges terminés</div>
                                    </div>
                                    <div style={styles.statCard}>
                                        <div style={styles.statNumber}>{statsRelations.terminees_ce_mois}</div>
                                        <div style={styles.statLabel}>Terminés ce mois</div>
                                    </div>
                                    <div style={styles.statCard}>
                                        <div style={styles.statTexte}>
                                            {statsRelations.duree_moyenne !== null ? `${statsRelations.duree_moyenne} j` : '—'}
                                        </div>
                                        <div style={styles.statLabel}>Durée moyenne</div>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* ═══ Abonnements ═══ */}
                        {statsAbonnements && (
                            <>
                                <h3 style={{ ...styles.sousTitre, textAlign: isMobile ? 'center' : 'left' }}>Abonnements</h3>
                                <div style={{ ...styles.statsGrid, gridTemplateColumns: isSmall ? '1fr 1fr' : 'repeat(auto-fit, minmax(190px, 1fr))', gap: isSmall ? '12px' : '20px' }}>
                                    <div style={styles.statCard}>
                                        <div style={styles.statNumber}>{statsAbonnements.actifs}</div>
                                        <div style={styles.statLabel}>Abonnements actifs</div>
                                    </div>
                                    <div style={styles.statCard}>
                                        <div style={styles.statNumber}>{statsAbonnements.total}</div>
                                        <div style={styles.statLabel}>Total souscriptions</div>
                                    </div>
                                    <div style={styles.statCard}>
                                        <div style={styles.statTexte}>
                                            {statsAbonnements.revenus.toFixed(2).replace('.', ',')} €
                                        </div>
                                        <div style={styles.statLabel}>Revenus cumulés</div>
                                    </div>
                                    <div style={styles.statCard}>
                                        <div style={styles.statTexte}>
                                            {statsAbonnements.hommes} / {statsAbonnements.femmes}
                                        </div>
                                        <div style={styles.statLabel}>Hommes / Femmes abonnés</div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {activeTab === 'members' && (
                    <div>
                        <h2 style={{ ...styles.title, fontSize: isSmall ? '23px' : isMobile ? '26px' : '30px', textAlign: isMobile ? 'center' : 'left' }}>Gestion des membres</h2>

                        {/* ═══ Statistiques membres ═══ */}
                        <div style={{ ...styles.statsGrid, gridTemplateColumns: isSmall ? '1fr 1fr' : 'repeat(auto-fit, minmax(190px, 1fr))', gap: isSmall ? '12px' : '20px' }}>
                            <div style={styles.statCard}>
                                <div style={styles.statNumber}>{members.length}</div>
                                <div style={styles.statLabel}>Total inscrits</div>
                            </div>
                            <div style={styles.statCard}>
                                <div style={styles.statNumber}>
                                    {members.filter(m => m.is_validated).length}
                                </div>
                                <div style={styles.statLabel}>Membres validés</div>
                            </div>
                            <div style={styles.statCard}>
                                <div style={styles.statNumber}>
                                    {members.filter(m => !m.is_validated).length}
                                </div>
                                <div style={styles.statLabel}>En attente de validation</div>
                            </div>
                            <div style={styles.statCard}>
                                <div style={styles.statTexte}>
                                    {members.filter(m => m.gender === 'homme').length} / {members.filter(m => m.gender === 'femme').length}
                                </div>
                                <div style={styles.statLabel}>Hommes / Femmes</div>
                            </div>
                            <div style={styles.statCard}>
                                <div style={styles.statNumber}>
                                    {statsAbonnements ? statsAbonnements.actifs : '—'}
                                </div>
                                <div style={styles.statLabel}>Membres abonnés</div>
                            </div>
                            <div style={styles.statCard}>
                                <div style={styles.statNumber}>{nouveauxCeMois}</div>
                                <div style={styles.statLabel}>Inscrits ce mois</div>
                            </div>
                            <div style={styles.statCard}>
                                <div style={styles.statNumber}>{profilsComplets}</div>
                                <div style={styles.statLabel}>Profils complétés</div>
                            </div>
                            <div style={styles.statCard}>
                                <div style={styles.statNom} title={villePrincipale.nom}>{villePrincipale.nom}</div>
                                <div style={styles.statLabel}>
                                    Ville la plus représentée
                                </div>
                            </div>
                        </div>

                        {/* ═══ Filtres, tableau et pagination dans un bloc unique ═══ */}
                        <div style={styles.blocTableau}>
                            <div style={{ ...styles.filtresCard, padding: isSmall ? '14px 16px' : '18px 20px' }}>
                                <div style={{ ...styles.filtresRow, flexDirection: isSmall ? 'column' : 'row', alignItems: isSmall ? 'stretch' : 'flex-end', gap: isSmall ? '10px' : '14px' }}>
                                    <label style={filtreLabelStyle}>
                                        Rechercher
                                        <input
                                            value={mRecherche}
                                            onChange={e => { setMRecherche(e.target.value); setMPage(1); }}
                                            placeholder="Kounya, email, ville ou ID"
                                            style={styles.filtreInput}
                                        />
                                    </label>

                                    <label style={filtreLabelStyle}>
                                        Statut
                                        <select value={mStatut} onChange={e => { setMStatut(e.target.value); setMPage(1); }} style={styles.filtreInput}>
                                            <option value="">Tous</option>
                                            <option value="valides">Validés</option>
                                            <option value="attente">En attente</option>
                                        </select>
                                    </label>

                                    <label style={filtreLabelStyle}>
                                        Genre
                                        <select value={mGenre} onChange={e => { setMGenre(e.target.value); setMPage(1); }} style={styles.filtreInput}>
                                            <option value="">Tous</option>
                                            <option value="homme">Hommes</option>
                                            <option value="femme">Femmes</option>
                                        </select>
                                    </label>

                                    <label style={filtreLabelStyle}>
                                        Ville
                                        <select value={mVille} onChange={e => { setMVille(e.target.value); setMPage(1); }} style={styles.filtreInput}>
                                            <option value="">Toutes</option>
                                            {villesDisponibles.map(v => <option key={v} value={v}>{v}</option>)}
                                        </select>
                                    </label>

                                    {mFiltresActifs > 0 && (
                                        <button onClick={resetFiltresMembres} style={styles.resetFiltreBtn}>
                                            Réinitialiser
                                        </button>
                                    )}
                                </div>
                            </div>

                            {loading ? (
                                <div style={styles.loading}>Chargement...</div>
                            ) : membresFiltres.length === 0 ? (
                                <div style={styles.empty}>Aucun membre ne correspond à ces critères.</div>
                            ) : (
                                <>
                                    <div style={styles.tableScroll}><table style={styles.table}>
                                        <thead>
                                            <tr style={styles.tableHeader}>
                                                <th style={styles.th}>Email</th>
                                                <th style={styles.th}>Genre</th>
                                                <th style={styles.th}>Nom</th>
                                                <th style={styles.th}>Ville</th>
                                                <th style={styles.th}>Statut</th>
                                                <th style={styles.th}>Rôle</th>
                                                <th style={styles.th}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {membresAffiches.map(member => (
                                                <tr key={member.id} style={styles.tableRow}>
                                                    <td style={styles.td}>{member.email}</td>
                                                    <td style={styles.td}>{member.gender}</td>
                                                    <td style={styles.td}>{member.first_name || '—'}</td>
                                                    <td style={styles.td}>{member.city || '—'}</td>
                                                    <td style={styles.td}>
                                                        <span style={member.is_validated ? styles.validated : (member.statut_profil === 'refuse' ? styles.refused : (member.statut_profil === 'brouillon' ? styles.brouillon : styles.pending))}>
                                                            {member.is_validated ? '✅ Validé' : (member.statut_profil === 'refuse' ? '✖ Refusé' : (member.statut_profil === 'brouillon' ? '✏ Brouillon' : '⏳ En attente'))}
                                                        </span>
                                                    </td>
                                                    <td style={styles.td}>
                                                        {member.role === 'admin' ? '👑 Admin'
                                                            : member.role === 'moderateur' ? '🛡️ Modérateur'
                                                                : 'Membre'}
                                                    </td>
                                                    <td style={styles.td}>
                                                        <div style={styles.actionsBtns}>
                                                            <button onClick={() => handleVoirProfil(member)} style={styles.voirBtn}>
                                                                Voir le profil
                                                            </button>
                                                            {!member.is_validated && member.statut_profil !== 'brouillon' && (
                                                                <button onClick={() => handleValidate(member.id)} style={styles.validateBtn}>
                                                                    Valider
                                                                </button>
                                                            )}
                                                            {member.role !== 'admin' && member.role !== 'moderateur' && (
                                                                <button onClick={() => { setRefusTarget(member); setRefusMotif(''); }} style={styles.refuseBtn}>
                                                                    Refuser
                                                                </button>
                                                            )}
                                                            {/* Bouton de gestion du rôle modérateur (jamais sur un admin) */}
                                                            {member.role !== 'admin' && (
                                                                member.role === 'moderateur' ? (
                                                                    <button onClick={() => handleChangeRole(member.id, 'member')} style={styles.demoteBtn}>
                                                                        Retirer modérateur
                                                                    </button>
                                                                ) : (
                                                                    <button onClick={() => handleChangeRole(member.id, 'moderateur')} style={styles.promoteBtn}>
                                                                        Nommer modérateur
                                                                    </button>
                                                                )
                                                            )}
                                                            <button onClick={() => handleDelete(member.id)} style={styles.deleteBtn}>
                                                                Supprimer
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table></div>

                                    {/* ═══ Pagination ═══ */}
                                    <div style={styles.pagination}>
                                        <span style={styles.paginationInfo}>
                                            {membresFiltres.length} membre{membresFiltres.length > 1 ? 's' : ''}
                                            {mFiltresActifs > 0 ? ' (filtrés)' : ''}
                                            {' · '}page {mPageCourante} sur {mTotalPages}
                                        </span>
                                        {mTotalPages > 1 && (
                                            <div style={styles.paginationBtns}>
                                                <button
                                                    onClick={() => setMPage(p => Math.max(1, p - 1))}
                                                    disabled={mPageCourante === 1}
                                                    style={mPageCourante === 1 ? styles.pageBtnDisabled : styles.pageBtn}
                                                >
                                                    ‹ Précédent
                                                </button>
                                                <button
                                                    onClick={() => setMPage(p => Math.min(mTotalPages, p + 1))}
                                                    disabled={mPageCourante === mTotalPages}
                                                    style={mPageCourante === mTotalPages ? styles.pageBtnDisabled : styles.pageBtn}
                                                >
                                                    Suivant ›
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === 'messages' && (
                    <div>
                        <h2 style={{ ...styles.title, fontSize: isSmall ? '23px' : isMobile ? '26px' : '30px', textAlign: isMobile ? 'center' : 'left' }}>Modération des messages</h2>

                        {/* ═══ HORAIRES DE MODÉRATION ═══ */}
                        <div style={styles.horairesCard}>
                            <div style={styles.horairesHead}>
                                <div>
                                    <h3 style={styles.horairesTitre}>Horaires de modération</h3>
                                    <p style={styles.horairesDesc}>
                                        Plage pendant laquelle les messages sont traités (heure de Paris).
                                        En dehors, les messages restent en attente et sont transmis à l'ouverture.
                                    </p>
                                </div>
                                <span style={horaires.ouvert ? styles.badgeOuvert : styles.badgeFerme}>
                                    {horaires.ouvert ? '● Ouvert' : '● Fermé'}
                                </span>
                            </div>

                            <div style={{ ...styles.horairesRow, flexDirection: isSmall ? 'column' : 'row', alignItems: isSmall ? 'stretch' : 'center' }}>
                                {/* Les deux champs De / à restent toujours côte à côte */}
                                <div style={{ display: 'flex', flexDirection: 'row', gap: '14px', alignItems: 'center' }}>
                                    <label style={styles.horairesLabel}>
                                        De
                                        <select
                                            value={horairesEdit.heure_debut}
                                            onChange={(e) => setHorairesEdit({ ...horairesEdit, heure_debut: Number(e.target.value) })}
                                            style={styles.horairesSelect}
                                            disabled={!horairesEdit.actif}
                                        >
                                            {Array.from({ length: 24 }, (_, h) => (
                                                <option key={h} value={h}>{String(h).padStart(2, '0')}h</option>
                                            ))}
                                        </select>
                                    </label>

                                    <label style={styles.horairesLabel}>
                                        à
                                        <select
                                            value={horairesEdit.heure_fin}
                                            onChange={(e) => setHorairesEdit({ ...horairesEdit, heure_fin: Number(e.target.value) })}
                                            style={styles.horairesSelect}
                                            disabled={!horairesEdit.actif}
                                        >
                                            {Array.from({ length: 24 }, (_, h) => (
                                                <option key={h} value={h}>{String(h).padStart(2, '0')}h</option>
                                            ))}
                                        </select>
                                    </label>
                                </div>

                                <label style={styles.horairesCheck}>
                                    <input
                                        type="checkbox"
                                        checked={!horairesEdit.actif}
                                        onChange={(e) => setHorairesEdit({ ...horairesEdit, actif: !e.target.checked })}
                                    />
                                    Modération continue (24h/24)
                                </label>

                                <label style={styles.horairesLabel}>
                                    Jour de fermeture
                                    <select
                                        value={horairesEdit.jour_ferme === null || horairesEdit.jour_ferme === undefined ? '' : horairesEdit.jour_ferme}
                                        onChange={(e) => setHorairesEdit({ ...horairesEdit, jour_ferme: e.target.value === '' ? null : Number(e.target.value) })}
                                        style={styles.horairesSelect}
                                    >
                                        <option value="">Aucun (7j/7)</option>
                                        <option value={1}>Lundi</option>
                                        <option value={2}>Mardi</option>
                                        <option value={3}>Mercredi</option>
                                        <option value={4}>Jeudi</option>
                                        <option value={5}>Vendredi</option>
                                        <option value={6}>Samedi</option>
                                        <option value={0}>Dimanche</option>
                                    </select>
                                </label>

                                <button onClick={handleSaveHoraires} style={styles.horairesBtn}>
                                    Enregistrer
                                </button>
                            </div>

                            {horairesMsg && <div style={styles.horairesMsg}>{horairesMsg}</div>}
                        </div>
                        {pendingMessages.length === 0 ? (
                            <div style={styles.empty}>
                                ✅ Aucun message en attente de modération.
                            </div>
                        ) : (
                            <div style={styles.messagesList}>
                                {pendingMessages.map(msg => (
                                    <div key={msg.id} style={styles.messageCard}>
                                        <div style={styles.messageHeader}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
                                                {/* Expéditeur */}
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    {msg.sender_photo ? (
                                                        <img src={msg.sender_photo} alt={msg.sender_name} style={styles.avatarImg} />
                                                    ) : (
                                                        <div style={styles.avatar}>
                                                            {(msg.sender_name || msg.sender_email).charAt(0).toUpperCase()}
                                                        </div>
                                                    )}
                                                    <div>
                                                        <span style={{ display: 'block', color: '#aaa', fontSize: '11px', fontWeight: '600' }}>📤 Expéditeur</span>
                                                        <span style={{ color: '#3D0F1E', fontSize: '14px', fontWeight: '700' }}>{msg.sender_name || 'Sans nom'}</span>
                                                    </div>
                                                </div>

                                                {/* Flèche */}
                                                <span style={{ color: '#C4688A', fontSize: '20px', fontWeight: '700' }}>→</span>

                                                {/* Destinataire */}
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    {msg.receiver_photo ? (
                                                        <img src={msg.receiver_photo} alt={msg.receiver_name} style={styles.avatarImg} />
                                                    ) : (
                                                        <div style={{ ...styles.avatar, backgroundColor: '#6B1E35' }}>
                                                            {(msg.receiver_name || msg.receiver_email).charAt(0).toUpperCase()}
                                                        </div>
                                                    )}
                                                    <div>
                                                        <span style={{ display: 'block', color: '#aaa', fontSize: '11px', fontWeight: '600' }}>📥 Destinataire</span>
                                                        <span style={{ color: '#6B1E35', fontSize: '14px', fontWeight: '700' }}>{msg.receiver_name || 'Sans nom'}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <span style={styles.messageDate}>
                                                {new Date(msg.created_at).toLocaleDateString('fr-FR')}
                                            </span>
                                        </div>
                                        <p style={styles.messageContent}>{msg.content}</p>
                                        <div style={styles.messageActions}>
                                            <button onClick={() => handleApproveMessage(msg.id)} style={styles.approveBtn}>
                                                ✅ Approuver
                                            </button>
                                            <button onClick={() => openRejectModal(msg)} style={styles.rejectBtn}>
                                                ❌ Rejeter
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'contact' && (
                    <div>
                        <h2 style={{ ...styles.title, fontSize: isSmall ? '23px' : isMobile ? '26px' : '30px', textAlign: isMobile ? 'center' : 'left' }}>Messages reçus via le formulaire de contact</h2>
                        {contactMessages.length === 0 ? (
                            <div style={styles.empty}>
                                Aucun message de contact pour le moment.
                            </div>
                        ) : (
                            <div style={styles.messagesList}>
                                {contactMessages.map(msg => (
                                    <div key={msg.id} style={{ ...styles.messageCard, border: msg.is_read ? '2px solid #EADCE2' : '2px solid #C4688A' }}>
                                        <div style={styles.messageHeader}>
                                            <div>
                                                <span style={styles.messageFrom}>
                                                    👤 <strong>{msg.name}</strong> — {msg.email}
                                                </span>
                                                <span style={styles.messageTo}>
                                                    📋 Sujet : <strong>{msg.subject || 'Non précisé'}</strong>
                                                </span>
                                            </div>
                                            <span style={styles.messageDate}>
                                                {new Date(msg.created_at).toLocaleDateString('fr-FR')}
                                                {!msg.is_read && <span style={{ marginLeft: '8px', color: '#C4688A', fontWeight: '700' }}>● Nouveau</span>}
                                            </span>
                                        </div>
                                        <p style={styles.messageContent}>{msg.message}</p>
                                        <div style={styles.messageActions}>
                                            <a href={`https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(msg.email)}&su=${encodeURIComponent('Re: ' + (msg.subject || 'Votre message'))}&body=${encodeURIComponent('Bonjour ' + msg.name + ',\n\n')}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ ...styles.approveBtn, textDecoration: 'none', display: 'inline-block' }}
                                            >
                                                ✉️ Répondre
                                            </a>
                                            {!msg.is_read && (
                                                <button onClick={() => handleMarkRead(msg.id)} style={styles.validateBtn}>
                                                    ✅ Marquer comme lu
                                                </button>
                                            )}
                                            <button onClick={() => handleDeleteContact(msg.id)} style={styles.rejectBtn}>
                                                🗑️ Supprimer
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'abonnements' && <AdminAbonnements />}
                {activeTab === 'historique' && <AdminHistorique />}
            </div>

            {/* ═══ MODALE DE REJET AVEC MOTIF ═══ */}
            {rejectTarget && (
                <div style={styles.rejectOverlay} onClick={() => !rejectLoading && setRejectTarget(null)}>
                    <div style={{ ...styles.rejectModal, padding: isSmall ? '20px 16px' : '28px' }} onClick={(e) => e.stopPropagation()}>
                        <h3 style={styles.rejectTitle}>Rejeter ce message</h3>
                        <p style={styles.rejectSub}>
                            L'expéditeur <strong>{rejectTarget.sender_name || rejectTarget.sender_email}</strong> sera
                            informé du motif de refus.
                        </p>

                        <div style={styles.rejectMsgPreview}>« {rejectTarget.content} »</div>

                        {/* En-tête motif + bascule gestion */}
                        <div style={styles.motifHeader}>
                            <label style={styles.rejectLabel}>Motif du refus</label>
                            <button
                                type="button"
                                onClick={() => { setGestionMotifs(!gestionMotifs); setMotifEnEdition(null); setRejectError(''); }}
                                style={styles.gererLink}
                            >
                                {gestionMotifs ? '✓ Terminé' : '⚙ Gérer les motifs'}
                            </button>
                        </div>

                        {/* ─── Mode SÉLECTION (rejet normal) ─── */}
                        {!gestionMotifs && (
                            <div style={styles.motifList}>
                                {motifs.map((m) => (
                                    <button
                                        key={m.id}
                                        onClick={() => setRejectMotif(m.libelle)}
                                        style={rejectMotif === m.libelle ? styles.motifChipActive : styles.motifChip}
                                    >
                                        {m.libelle}
                                    </button>
                                ))}
                                {motifs.length === 0 && (
                                    <span style={{ color: '#9a7d88', fontSize: '13px' }}>
                                        Aucun motif. Cliquez sur « Gérer les motifs » pour en ajouter.
                                    </span>
                                )}
                            </div>
                        )}

                        {/* ─── Mode GESTION (ajout / modif / suppression) ─── */}
                        {gestionMotifs && (
                            <div style={styles.gestionBox}>
                                {motifs.map((m) => (
                                    <div key={m.id} style={styles.gestionLigne}>
                                        {motifEnEdition && motifEnEdition.id === m.id ? (
                                            <>
                                                <input
                                                    value={motifEnEdition.libelle}
                                                    onChange={(e) => setMotifEnEdition({ ...motifEnEdition, libelle: e.target.value })}
                                                    style={styles.gestionInput}
                                                    maxLength={150}
                                                    autoFocus
                                                />
                                                <button onClick={handleModifierMotif} style={styles.gestionOk}>✓</button>
                                                <button onClick={() => setMotifEnEdition(null)} style={styles.gestionAnnul}>✕</button>
                                            </>
                                        ) : (
                                            <>
                                                <span style={styles.gestionLibelle}>{m.libelle}</span>
                                                <button
                                                    onClick={() => setMotifEnEdition({ id: m.id, libelle: m.libelle })}
                                                    style={styles.gestionEdit}
                                                    title="Modifier"
                                                >
                                                    ✎
                                                </button>
                                                <button
                                                    onClick={() => handleSupprimerMotif(m.id)}
                                                    style={styles.gestionSuppr}
                                                    title="Supprimer"
                                                >
                                                    🗑
                                                </button>
                                            </>
                                        )}
                                    </div>
                                ))}

                                {/* Ajout d'un nouveau motif */}
                                <div style={styles.gestionAjout}>
                                    <input
                                        value={nouveauMotif}
                                        onChange={(e) => setNouveauMotif(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') handleAjouterMotif(); }}
                                        placeholder="Nouveau motif..."
                                        style={styles.gestionInput}
                                        maxLength={150}
                                    />
                                    <button onClick={handleAjouterMotif} style={styles.gestionAdd}>+ Ajouter</button>
                                </div>
                            </div>
                        )}

                        {/* Commentaire : seulement en mode sélection */}
                        {!gestionMotifs && (
                            <>
                                <label style={styles.rejectLabel}>Commentaire (facultatif)</label>
                                <textarea
                                    value={rejectComment}
                                    onChange={(e) => setRejectComment(e.target.value)}
                                    placeholder="Précisez si nécessaire..."
                                    style={styles.rejectTextarea}
                                    rows={3}
                                    maxLength={250}
                                />
                            </>
                        )}

                        {rejectError && <div style={styles.rejectErr}>{rejectError}</div>}

                        {/* Actions : seulement en mode sélection */}
                        {!gestionMotifs && (
                            <div style={styles.rejectActions}>
                                <button
                                    onClick={() => setRejectTarget(null)}
                                    disabled={rejectLoading}
                                    style={styles.rejectCancel}
                                >
                                    Annuler
                                </button>
                                <button
                                    onClick={handleConfirmReject}
                                    disabled={rejectLoading}
                                    style={styles.rejectConfirm}
                                >
                                    {rejectLoading ? 'Rejet...' : 'Confirmer le rejet'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ═══ Modale : voir le profil complet d'un membre ═══ */}
            {profilVu && (
                <div style={styles.modalOverlay} onClick={() => setProfilVu(null)}>
                    <div style={styles.modalProfil} onClick={e => e.stopPropagation()}>
                        <div style={styles.modalHeader}>
                            <h3 style={{ margin: 0, color: '#6B1E35' }}>Profil de {profilVu.email}</h3>
                            <button onClick={() => setProfilVu(null)} style={styles.modalClose}>✕</button>
                        </div>
                        {profilVu.__chargement ? (
                            <p>Chargement…</p>
                        ) : profilVu.__erreur ? (
                            <p style={{ color: '#c00' }}>Impossible de charger ce profil.</p>
                        ) : (
                            <div style={styles.profilGrille}>
                                {profilVu.photo_url && (
                                    <img src={profilVu.photo_url} alt="" style={styles.profilPhoto} />
                                )}
                                {[
                                    ['ID', profilVu.numero_membre ? `#${profilVu.numero_membre}` : null],
                                    ['Kounya', profilVu.first_name],
                                    ['Genre', profilVu.gender],
                                    ['Âge', profilVu.age],
                                    ['Date de naissance', profilVu.date_naissance],
                                    ['Taille (cm)', profilVu.taille],
                                    ['Poids (kg)', profilVu.poids],
                                    ['Situation', profilVu.marital_status],
                                    ['Enfants', profilVu.children],
                                    ['Enfant(s) souhaité(s)', profilVu.enfant_souhaite],
                                    ['Nombre d\'épouses', profilVu.nombre_epouse],
                                    ['Ville', profilVu.city],
                                    ['Pays', profilVu.country],
                                    ['Pays d\'origine', profilVu.pays_origine],
                                    ['Pays de résidence', profilVu.pays_residence],
                                    ['Ethnie', profilVu.ethnie],
                                    ['Langues', profilVu.langues],
                                    ['Pratique religieuse', profilVu.pratique_religieuse],
                                    ['Prière', profilVu.priere],
                                    ['Porte le voile', profilVu.porte_voile],
                                    ['Barbe', profilVu.barbe],
                                    ['École de pensée', profilVu.ecole_pensee],
                                    ['Converti(e)', profilVu.converti],
                                    ['Profession', profilVu.profession],
                                    ['Niveau d\'études', profilVu.education],
                                    ['Présentation', profilVu.bio],
                                    ['Critères rédhibitoires', profilVu.criteres_redhibitoires],
                                ].filter(([, v]) => v !== null && v !== undefined && v !== '').map(([label, v]) => (
                                    <div key={label} style={styles.profilLigne}>
                                        <span style={styles.profilLabel}>{label}</span>
                                        <span style={styles.profilValeur}>{String(v)}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ═══ Modale : refuser un profil avec motif ═══ */}
            {refusTarget && (
                <div style={styles.modalOverlay} onClick={() => setRefusTarget(null)}>
                    <div style={styles.modalRefus} onClick={e => e.stopPropagation()}>
                        <div style={styles.modalHeader}>
                            <h3 style={{ margin: 0, color: '#6B1E35' }}>Refuser le profil</h3>
                            <button onClick={() => setRefusTarget(null)} style={styles.modalClose}>✕</button>
                        </div>
                        <p style={{ color: '#5a4a52', fontSize: '14px' }}>
                            Indiquez le motif du refus. Le membre le recevra par email et pourra corriger puis renvoyer son profil.
                        </p>
                        <textarea
                            value={refusMotif}
                            onChange={e => setRefusMotif(e.target.value)}
                            placeholder="Ex : Photo de profil manquante, présentation incomplète…"
                            style={styles.refusTextarea}
                            rows={4}
                        />
                        <div style={{ display: 'flex', gap: '10px', marginTop: '12px', justifyContent: 'flex-end' }}>
                            <button onClick={() => setRefusTarget(null)} style={styles.refusAnnuler}>Annuler</button>
                            <button onClick={handleRefuser} disabled={refusLoading || !refusMotif.trim()} style={styles.refusConfirmer}>
                                {refusLoading ? 'Envoi…' : 'Confirmer le refus'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

const styles = {
    // ═══ Horaires de modération ═══
    horairesCard: { backgroundColor: 'white', border: '1px solid #EADCE2', borderRadius: '12px', padding: '20px 22px', marginBottom: '24px', boxShadow: '0 4px 15px rgba(0,0,0,0.06)' },
    horairesHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' },
    horairesTitre: { color: '#3D0F1E', fontSize: '19px', fontWeight: '700', margin: '0 0 6px', fontFamily: "'Cormorant Garamond', Georgia, serif" },
    horairesDesc: { color: '#9a7d88', fontSize: '13px', lineHeight: '1.5', margin: 0, maxWidth: '520px' },
    badgeOuvert: { backgroundColor: 'rgba(44,95,46,0.12)', color: '#2c5f2e', padding: '6px 14px', borderRadius: '20px', fontSize: '12.5px', fontWeight: '800', whiteSpace: 'nowrap' },
    badgeFerme: { backgroundColor: 'rgba(154,125,136,0.15)', color: '#9a7d88', padding: '6px 14px', borderRadius: '20px', fontSize: '12.5px', fontWeight: '800', whiteSpace: 'nowrap' },
    horairesRow: { display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' },
    horairesLabel: { display: 'inline-flex', alignItems: 'center', gap: '8px', color: '#3D0F1E', fontSize: '14px', fontWeight: '700' },
    horairesSelect: { padding: '8px 10px', border: '1.5px solid #EADCE2', borderRadius: '8px', fontSize: '14px', color: '#3D0F1E', outline: 'none', cursor: 'pointer' },
    horairesCheck: { display: 'inline-flex', alignItems: 'center', gap: '7px', color: '#666', fontSize: '13px', cursor: 'pointer' },
    horairesBtn: { padding: '10px 22px', width: 'auto', background: 'linear-gradient(135deg, #3D0F1E, #6B1E35)', color: '#FBF3EC', border: 'none', borderRadius: '8px', fontWeight: '700', fontSize: '13.5px', cursor: 'pointer', marginLeft: 'auto' },
    horairesMsg: { marginTop: '12px', padding: '9px 12px', backgroundColor: '#f0fff0', color: '#2c5f2e', borderRadius: '8px', fontSize: '13px', textAlign: 'center' },

    // ═══ Modale de rejet avec motif ═══
    rejectOverlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px', boxSizing: 'border-box' },
    rejectModal: { backgroundColor: '#fff', borderRadius: '16px', padding: '28px', width: '100%', maxWidth: '520px', boxShadow: '0 20px 60px rgba(0,0,0,0.35)', boxSizing: 'border-box', maxHeight: '90vh', overflowY: 'auto' },
    rejectTitle: { color: '#3D0F1E', fontSize: '24px', fontWeight: '700', margin: '0 0 8px', fontFamily: "'Cormorant Garamond', Georgia, serif" },
    rejectSub: { color: '#9a7d88', fontSize: '13.5px', lineHeight: '1.5', margin: '0 0 16px' },
    rejectMsgPreview: { background: '#FBF6F1', border: '1px solid #F0E4E9', borderRadius: '10px', padding: '12px 14px', color: '#5a4a52', fontSize: '13.5px', fontStyle: 'italic', lineHeight: '1.5', marginBottom: '20px' },
    rejectLabel: { display: 'block', color: '#6B1E35', fontSize: '13px', fontWeight: '700', margin: '0 0 8px' },
    motifList: { display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' },
    motifHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' },
    gererLink: { background: 'transparent', border: 'none', color: '#6B1E35', fontSize: '12.5px', fontWeight: '700', cursor: 'pointer', padding: '2px 4px' },
    gestionBox: { background: '#FBF6F1', border: '1px solid #F0E4E9', borderRadius: '10px', padding: '12px', marginBottom: '20px' },
    gestionLigne: { display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0', borderBottom: '1px solid #F0E4E9' },
    gestionLibelle: { flex: 1, color: '#3D0F1E', fontSize: '13px' },
    gestionInput: { flex: 1, padding: '7px 10px', border: '1.5px solid #E8CBD6', borderRadius: '8px', fontSize: '13px', outline: 'none', color: '#3a3a3a', minWidth: 0 },
    gestionEdit: { background: 'rgba(201,168,76,0.2)', border: 'none', borderRadius: '6px', width: '28px', height: '28px', cursor: 'pointer', color: '#6B1E35', fontSize: '13px', flexShrink: 0 },
    gestionSuppr: { background: 'rgba(204,0,0,0.1)', border: 'none', borderRadius: '6px', width: '28px', height: '28px', cursor: 'pointer', fontSize: '13px', flexShrink: 0 },
    gestionOk: { background: '#2c5f2e', border: 'none', borderRadius: '6px', width: '28px', height: '28px', cursor: 'pointer', color: '#fff', fontSize: '13px', flexShrink: 0 },
    gestionAnnul: { background: '#fff', border: '1px solid #E8CBD6', borderRadius: '6px', width: '28px', height: '28px', cursor: 'pointer', color: '#6B1E35', fontSize: '13px', flexShrink: 0 },
    gestionAjout: { display: 'flex', gap: '8px', marginTop: '12px' },
    gestionAdd: { background: 'linear-gradient(135deg, #3D0F1E, #6B1E35)', color: '#FBF3EC', border: 'none', borderRadius: '8px', padding: '7px 14px', fontSize: '12.5px', fontWeight: '700', cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' },
    motifChip: { padding: '8px 14px', backgroundColor: '#fff', color: '#6B1E35', border: '1.5px solid #E8CBD6', borderRadius: '30px', fontSize: '12.5px', fontWeight: '600', cursor: 'pointer', textAlign: 'left' },
    motifChipActive: { padding: '8px 14px', background: 'linear-gradient(135deg, #3D0F1E, #6B1E35)', color: '#fff', border: '1.5px solid #3D0F1E', borderRadius: '30px', fontSize: '12.5px', fontWeight: '700', cursor: 'pointer', textAlign: 'left', boxShadow: '0 3px 12px rgba(74,0,40,0.25)' },
    rejectTextarea: { width: '100%', padding: '12px 14px', borderRadius: '10px', border: '1.5px solid #E8CBD6', fontSize: '14px', resize: 'vertical', boxSizing: 'border-box', outline: 'none', color: '#3a3a3a', marginBottom: '16px' },
    rejectErr: { backgroundColor: '#ffe0e0', color: '#c00', padding: '10px 12px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px', textAlign: 'center' },
    rejectActions: { display: 'flex', gap: '12px' },
    rejectCancel: { flex: 1, padding: '12px', backgroundColor: '#fff', color: '#6B1E35', border: '1.5px solid #E8CBD6', borderRadius: '10px', fontWeight: '700', fontSize: '14px', cursor: 'pointer' },
    rejectConfirm: { flex: 2, padding: '12px', backgroundColor: '#c00', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: '700', fontSize: '14px', cursor: 'pointer' },

    avatarImg: {
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        objectFit: 'cover',
        flexShrink: 0,
    },
    container: {
        minHeight: '100vh',
        width: '100%',
        maxWidth: '100vw',
        overflowX: 'hidden',
        boxSizing: 'border-box',
        backgroundColor: '#FBF6F1',
        fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
    },

    // ═══ NAVBAR style NavbarAuth ═══
    navbar: {
        width: '100%',
        maxWidth: '100vw',
        boxSizing: 'border-box',
        height: '72px',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        boxShadow: '0 2px 10px rgba(0,0,0,0.4)',
        backgroundColor: '#3D0F1E',
        backgroundImage: `url(${fleur})`,
        backgroundSize: 'auto 120%',
        backgroundPosition: 'left center',
        backgroundRepeat: 'no-repeat',
        padding: '0 48px',
    },
    navLogo: {
        width: 'auto',
        height: '160px',
        marginLeft: '45px',
        marginTop: '25px',
        cursor: 'pointer',
    },
    navCenter: { display: 'flex', gap: '8px', alignItems: 'center', marginLeft: 'auto' },
    navRight: { display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto', flexShrink: 0 },

    // ═══ Menu mobile ═══
    burgerBtn: {
        position: 'relative',
        padding: '8px 12px',
        background: 'linear-gradient(135deg, #3D0F1E, #6B1E35)',
        border: '1px solid rgba(255,255,255,0.5)',
        borderRadius: '6px',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '38px',
        boxSizing: 'border-box',
    },
    mobileMenu: {
        position: 'sticky',
        top: '150px',
        zIndex: 99,
        backgroundColor: '#2A0A14',
        display: 'flex',
        flexDirection: 'column',
        padding: '10px',
        gap: '6px',
        boxShadow: '0 6px 18px rgba(0,0,0,0.3)',
        maxHeight: 'calc(100vh - 150px)',
        overflowY: 'auto',
    },
    mobileTab: {
        position: 'relative',
        padding: '13px 16px',
        background: 'transparent',
        color: 'white',
        border: '1px solid rgba(255,255,255,0.25)',
        borderRadius: '8px',
        cursor: 'pointer',
        fontWeight: '500',
        fontSize: '14.5px',
        textAlign: 'left',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    mobileTabActive: {
        position: 'relative',
        padding: '13px 16px',
        background: 'linear-gradient(135deg, #6B1E35, #C4688A)',
        color: 'white',
        border: '1px solid rgba(255,255,255,0.6)',
        borderRadius: '8px',
        cursor: 'pointer',
        fontWeight: '700',
        fontSize: '14.5px',
        textAlign: 'left',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    mobileLogout: {
        marginTop: '6px',
        padding: '13px 16px',
        background: 'transparent',
        color: '#C4688A',
        border: '1px solid rgba(196,104,138,0.5)',
        borderRadius: '8px',
        cursor: 'pointer',
        fontWeight: '700',
        fontSize: '14.5px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '9px',
    },
    mobileBadge: {
        backgroundColor: '#C4688A',
        color: 'white',
        borderRadius: '12px',
        minWidth: '22px',
        height: '22px',
        fontSize: '12px',
        fontWeight: '800',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 7px',
    },
    navUserBox: { display: 'flex', alignItems: 'center', gap: '10px' },
    navAvatar: {
        width: '38px', height: '38px', borderRadius: '50%', overflow: 'hidden',
        border: '2px solid white', display: 'flex', alignItems: 'center', justifyContent: 'center',
        backgroundColor: '#FBF3EC', flexShrink: 0,
    },
    navUser: { color: 'white', fontWeight: '600', fontSize: '14px' },
    tab: {
        position: 'relative',
        padding: '8px 16px',
        background: 'linear-gradient(135deg, #3D0F1E, #6B1E35)',
        color: 'white',
        border: '1px solid rgba(255,255,255,0.5)',
        borderRadius: '6px',
        cursor: 'pointer',
        fontWeight: '500',
    },
    tabActive: {
        position: 'relative',
        padding: '8px 16px',
        backgroundColor: '#2A0A14',
        color: 'white',
        border: '1px solid white',
        borderRadius: '6px',
        cursor: 'pointer',
        fontWeight: '700',
    },
    badge: {
        position: 'absolute',
        top: '-6px',
        right: '-6px',
        backgroundColor: '#C4688A',
        color: '#3D0F1E',
        borderRadius: '50%',
        minWidth: '18px',
        height: '18px',
        fontSize: '11px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: '700',
        padding: '0 4px',
        border: '2px solid #3D0F1E',
    },
    userConnecte: { color: '#f3e0c7', fontSize: '14px', fontWeight: '600', marginRight: '14px', whiteSpace: 'nowrap' },
    userConnecteMobile: { color: '#c0306a', fontSize: '14px', fontWeight: '700', padding: '10px 4px', textAlign: 'center', borderBottom: '1px solid rgba(192,48,106,0.2)', marginBottom: '6px' },
    logoutBtn: {
        padding: '8px 12px',
        background: 'linear-gradient(135deg, #3D0F1E, #6B1E35)',
        color: 'white',
        border: '1px solid rgba(255,255,255,0.5)',
        borderRadius: '6px',
        cursor: 'pointer',
        fontWeight: '700',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Aligne la hauteur sur celle des onglets (texte + padding)
        height: '38px',
        boxSizing: 'border-box',
    },

    content: { padding: '32px', maxWidth: '1200px', width: '100%', margin: '0 auto', boxSizing: 'border-box' },
    title: { color: '#3D0F1E', marginBottom: '24px', fontSize: '30px', fontWeight: '700', fontFamily: "'Cormorant Garamond', Georgia, serif", letterSpacing: '0.3px', wordBreak: 'break-word' },
    sousTitre: { color: '#3D0F1E', fontSize: '22px', fontWeight: '700', margin: '32px 0 18px', fontFamily: "'Cormorant Garamond', Georgia, serif", wordBreak: 'break-word' },
    statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '20px', marginBottom: '28px' },
    statCard: {
        backgroundColor: 'white',
        minWidth: 0,
        padding: '28px 20px',
        borderRadius: '12px',
        textAlign: 'center',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        border: '1px solid #EADCE2',
        // Hauteur uniforme : le chiffre est centré, le libellé toujours en bas
        minHeight: '150px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        boxSizing: 'border-box',
    },
    // Toutes les valeurs de statistique partagent la même taille,
    // pour un alignement visuel régulier entre les cartes.
    statNumber: {
        fontSize: '34px',
        fontWeight: '700',
        color: '#3D0F1E',
        fontFamily: "'Cormorant Garamond', Georgia, serif",
        lineHeight: 1.15,
        whiteSpace: 'nowrap',
    },
    statTexte: {
        fontSize: '34px',
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontWeight: '700',
        color: '#3D0F1E',
        fontFamily: "'Cormorant Garamond', Georgia, serif",
        lineHeight: 1.15,
        whiteSpace: 'nowrap',
    },
    statLabel: { fontSize: '15px', color: '#8A8A8A', lineHeight: 1.35 },
    // Valeur longue (nom de ville) : retour à la ligne autorisé,
    // légèrement réduite pour ne pas casser la hauteur des cartes.
    // Nom de ville : affiché en entier, sur deux lignes si nécessaire
    statNom: {
        fontSize: '20px',
        fontWeight: '700',
        color: '#3D0F1E',
        fontFamily: "'Cormorant Garamond', Georgia, serif",
        lineHeight: 1.25,
        maxWidth: '100%',
        // Coupure uniquement entre les mots, jamais au milieu
        overflowWrap: 'normal',
        hyphens: 'none',
    },
    loading: { textAlign: 'center', color: '#3D0F1E', fontSize: '18px' },
    empty: {
        textAlign: 'center',
        color: '#2c5f2e',
        fontSize: '18px',
        marginTop: '40px',
        padding: '32px',
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #EADCE2',
    },
    // ═══ Filtres et pagination ═══
    // Bloc unique : filtres, tableau et pagination dans une seule carte
    blocTableau: { backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 4px 15px rgba(0,0,0,0.06)', border: '1px solid #EADCE2', overflow: 'hidden' },
    filtresCard: { padding: '18px 20px', borderBottom: '1px solid #F0E4E9' },
    filtresRow: { display: 'flex', alignItems: 'flex-end', gap: '14px', flexWrap: 'wrap' },
    filtreLabel: { display: 'flex', flexDirection: 'column', gap: '6px', color: '#6B1E35', fontSize: '12.5px', fontWeight: '700', flex: '1 1 160px', minWidth: 0 },
    filtreInput: { padding: '9px 11px', border: '1.5px solid #EADCE2', borderRadius: '8px', fontSize: '13.5px', color: '#444', outline: 'none', width: '100%', boxSizing: 'border-box' },
    resetFiltreBtn: { padding: '10px 18px', backgroundColor: '#fff', color: '#6B1E35', border: '1.5px solid #EADCE2', borderRadius: '8px', fontWeight: '700', fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap' },

    pagination: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '14px', flexWrap: 'wrap', padding: '14px 20px', borderTop: '1px solid #F0E4E9' },
    paginationInfo: { color: '#8A8A8A', fontSize: '13px' },
    paginationBtns: { display: 'flex', gap: '8px' },
    pageBtn: { padding: '8px 16px', backgroundColor: '#fff', color: '#6B1E35', border: '1.5px solid #EADCE2', borderRadius: '8px', fontWeight: '700', fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap' },
    pageBtnDisabled: { padding: '8px 16px', backgroundColor: '#F5F5F7', color: '#BBB', border: '1.5px solid #EEE', borderRadius: '8px', fontWeight: '700', fontSize: '13px', cursor: 'not-allowed', whiteSpace: 'nowrap' },

    tableScroll: {
        width: '100%',
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        padding: '6px 20px 0',
        boxSizing: 'border-box',
    },
    table: {
        width: '100%',
        minWidth: '620px',
        borderCollapse: 'separate',
        borderSpacing: 0,
        backgroundColor: 'white',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
    },
    tableHeader: { backgroundColor: '#3D0F1E' },
    th: { padding: '14px 16px', color: '#C4688A', textAlign: 'left', fontWeight: '600' },
    tableRow: {},
    td: { padding: '12px 16px', color: '#444', borderBottom: '1px solid #F0E4E9' },
    validated: { color: '#2c5f2e', fontWeight: '600' },
    pending: { color: '#C4688A', fontWeight: '600' },
    refused: { color: '#c00', fontWeight: '600' },
    brouillon: { color: '#7a8899', fontWeight: '600' },
    voirBtn: { padding: '8px 12px', backgroundColor: '#c0306a', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', width: '100%', textAlign: 'center', fontSize: '13px', whiteSpace: 'nowrap' },
    refuseBtn: { padding: '8px 12px', backgroundColor: '#e08a00', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', width: '100%', textAlign: 'center', fontSize: '13px', whiteSpace: 'nowrap' },
    modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '16px' },
    modalProfil: { background: '#fff', borderRadius: '12px', padding: '20px', maxWidth: '560px', width: '100%', maxHeight: '85vh', overflowY: 'auto' },
    modalRefus: { background: '#fff', borderRadius: '12px', padding: '20px', maxWidth: '480px', width: '100%' },
    modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
    modalClose: { background: 'transparent', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#6B1E35' },
    profilGrille: { display: 'flex', flexDirection: 'column', gap: '8px' },
    profilPhoto: { width: '90px', height: '90px', borderRadius: '50%', objectFit: 'cover', alignSelf: 'center', marginBottom: '10px', border: '2px solid #c0306a' },
    profilLigne: { display: 'flex', justifyContent: 'space-between', gap: '12px', borderBottom: '1px solid #f0e6ea', paddingBottom: '6px' },
    profilLabel: { color: '#9a7d88', fontSize: '13px', fontWeight: '600', flexShrink: 0 },
    profilValeur: { color: '#3a3a3a', fontSize: '14px', textAlign: 'right' },
    refusTextarea: { width: '100%', padding: '12px', borderRadius: '8px', border: '1.5px solid #c0306a', fontSize: '14px', fontFamily: 'inherit', boxSizing: 'border-box', resize: 'vertical' },
    refusAnnuler: { padding: '10px 18px', background: '#eee', color: '#333', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' },
    refusConfirmer: { padding: '10px 18px', background: '#c00', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700' },
    actionsBtns: { display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'stretch', minWidth: '150px' },
    validateBtn: {
        padding: '8px 12px',
        backgroundColor: '#2c5f2e',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        width: '100%',
        textAlign: 'center',
        fontSize: '13px',
        whiteSpace: 'nowrap',
    },
    promoteBtn: {
        padding: '8px 12px',
        backgroundColor: '#6B1E35',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        width: '100%',
        textAlign: 'center',
        fontSize: '13px',
        whiteSpace: 'nowrap',
    },
    demoteBtn: {
        padding: '8px 12px',
        backgroundColor: '#9a7d88',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        width: '100%',
        textAlign: 'center',
        fontSize: '13px',
        whiteSpace: 'nowrap',
    },
    deleteBtn: {
        padding: '8px 12px',
        backgroundColor: '#c00',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        width: '100%',
        textAlign: 'center',
        fontSize: '13px',
        whiteSpace: 'nowrap',
    },
    messagesList: { display: 'flex', flexDirection: 'column', gap: '16px' },
    messageCard: {
        backgroundColor: 'white',
        padding: '18px',
        borderRadius: '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        border: '2px solid #C4688A',
    },
    messageHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '12px',
    },
    messageFrom: { display: 'block', color: '#3D0F1E', marginBottom: '4px' },
    messageTo: { display: 'block', color: '#6B1E35' },
    messageDate: { color: '#aaa', fontSize: '13px' },
    avatar: {
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        backgroundColor: '#3D0F1E',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '18px',
        fontWeight: '700',
        flexShrink: 0,
    },
    messageContent: {
        color: '#444',
        fontSize: '15px',
        padding: '12px',
        backgroundColor: '#FBF6F1',
        borderRadius: '8px',
        marginBottom: '16px',
    },
    messageActions: { display: 'flex', gap: '12px' },
    approveBtn: {
        padding: '8px 20px',
        backgroundColor: '#2c5f2e',
        color: 'white',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        fontWeight: '600',
    },
    rejectBtn: {
        padding: '8px 20px',
        backgroundColor: '#c00',
        color: 'white',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        fontWeight: '600',
    },
};

export default Admin;